import {
  format,
  parseISO,
  getQuarter as getCalendarQuarter,
  getYear as getCalendarYear,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfQuarter,
  subQuarters,
  subYears,
  lastDayOfQuarter,
  startOfYear,
  lastDayOfYear,
} from 'date-fns';
import { isEqual, sortBy } from 'lodash-es';

export const ISO_FORMAT = 'yyyy-MM-dd';
export const READABLE_FORMAT = 'MMM do, yyyy';
export const DISPLAY_DATE_FORMAT = 'MMM d, yyyy';
export const READABLE_FILE_NAME_FORMAT = 'yyyy-MMM-dd';
export const DEFAULT_DATE = format(new Date(), ISO_FORMAT);

export const isValidDate = (dateObj) => dateObj instanceof Date && !Number.isNaN(dateObj.valueOf());

export const parseDate = (date) => parseISO(date, new Date());

export const formatDate = (date, fmt) => format(parseDate(date), fmt);

/** Format a date string for UI display; returns '—' if falsy, or raw string on parse error. */
export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(parseDate(dateStr), DISPLAY_DATE_FORMAT);
  } catch {
    return dateStr;
  }
};

export const getQuarterYearLabel = (dateInput) => {
  if (!dateInput) return '';

  try {
    const date = parseDate(dateInput);

    if (!isValidDate(date)) return '';

    return `Q${getCalendarQuarter(date)} ${getCalendarYear(date)}`;
  } catch {
    return '';
  }
};

export const toFileNameFormat = (date) => {
  const parsed = parseDate(date);
  if (!isValidDate(parsed)) return '';
  return format(parsed, READABLE_FILE_NAME_FORMAT);
};

export const areDateRangesEqual = (dateRangeOne, dateRangeTwo) => isEqual(
  sortBy(dateRangeOne.map((date) => date.getTime())),
  sortBy(dateRangeTwo.map((date) => date.getTime())),
);

export const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const getDay = (date) => {
  const i = new Date(date).getUTCDay();
  return daysOfWeek[i];
};

export const getWeek = (weekFromCurrent) => {
  const rangeStart = startOfWeek(subWeeks(new Date(), weekFromCurrent));
  const rangeEnd = (weekFromCurrent === 0)
    ? new Date()
    : endOfWeek(subWeeks(new Date(), weekFromCurrent));
  return [rangeStart, rangeEnd];
};

export const getYear = (year) => {
  const rangeStart = startOfYear(subYears(new Date(), year));
  const rangeEnd = (year === 0)
    ? new Date()
    : lastDayOfYear(subYears(new Date(), year));
  return [rangeStart, rangeEnd];
};

export const getMonth = (date) => {
  const i = new Date(date).getMonth();
  return months[i];
};

export const getPreviousDays = ({
  rangeStartOffset,
  rangeEndOffset = 1,
}) => {
  const rangeStart = subDays(new Date(), rangeStartOffset);
  const rangeEnd = subDays(new Date(), rangeEndOffset);
  return [rangeStart, rangeEnd];
};

export const getLastWeek = () => {
  const rangeStart = startOfWeek(subWeeks(new Date(), 1));
  const rangeEnd = endOfWeek(subWeeks(new Date(), 1));
  return [rangeStart, rangeEnd];
};

export const getPreviousWeek = () => {
  const rangeStart = startOfWeek(subWeeks(new Date(), 2));
  const rangeEnd = endOfWeek(subWeeks(new Date(), 2));
  return [rangeStart, rangeEnd];
};

export const getPreviousMonth = () => {
  const rangeStart = startOfMonth(subMonths(new Date(), 1));
  const rangeEnd = endOfMonth(subMonths(new Date(), 1));
  return [rangeStart, rangeEnd];
};

export const getQuarter = (quarter) => {
  const rangeStart = startOfQuarter(subQuarters(new Date(), quarter));
  const rangeEnd = (quarter === 0)
    ? new Date()
    : lastDayOfQuarter(subQuarters(new Date(), quarter));
  return [rangeStart, rangeEnd];
};

export const getQuarterName = (quarterNum) => {
  const dateStr = (quarterNum === 0)
    ? DEFAULT_DATE
    : format(lastDayOfQuarter(subQuarters(new Date(), quarterNum)), ISO_FORMAT);
  const selectedDate = new Date(dateStr);
  const quarter = Math.floor((selectedDate.getMonth() + 3) / 3);
  const year = selectedDate.getFullYear();

  return quarter && year ? `Q${quarter} ${year}` : 'Date Range';
};

export const getLastThirtyDays = () => {
  const thirtyDaysAgo = format(subDays(new Date(), 30), ISO_FORMAT);
  return [thirtyDaysAgo, DEFAULT_DATE];
};

export const getCurrentMonth = () => {
  const rangeStart = startOfMonth(subMonths(new Date(), 0));
  const rangeEnd = subDays(new Date(), 0);
  return [rangeStart, rangeEnd];
};

export const formatRange = (dates) => {
  if (!dates.length || dates.every((currDate) => !currDate)) return 'Start Date - End Date';

  if (dates.length === 1) {
    const month = dates[0].getMonth() + 1;
    const day = dates[0].getDate();
    const year = dates[0].getFullYear();
    const start = `${month}/${day}/${year}`;

    return `${format(start, READABLE_FORMAT)} - End Date`;
  }

  return dates.map((currDate) => {
    const month = currDate.getMonth() + 1;
    const day = currDate.getDate();
    const year = currDate.getFullYear();
    const current = `${month}/${day}/${year}`;

    return `${format(current, READABLE_FORMAT)}`;
  }).join(' - ');
};
